import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import PublicContentPageShell from '@components/layout/PublicContentPageShell';
import { brandColors } from '@config/brandTokens';

const SUPPORT_TOPICS = [
  {
    title: 'Players & parents',
    body: 'Account help, self-evaluation, contests, certificates, and existing player subscriptions.',
  },
  {
    title: 'Academies & schools',
    body: 'Technical help after you are onboarded. For a yearly quote, use Contact sales on Subscriptions.',
  },
  {
    title: 'Sponsors & organizers',
    body: 'Partnership registration and event collaboration — also see Sponsors & Organizers.',
  },
];

const SupportPage: React.FC = () => {
  const [name, setName] = useState('');
  const [topic, setTopic] = useState('Players & parents');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/support-tickets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), topic, message: message.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to submit');
      }
      setSubmitted(true);
      setMessage('');
      setName('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicContentPageShell
      title="Support"
      subtitle="Questions about contests, subscriptions, academies, or schools? Submit a request — our customer support team usually replies within 1–2 business days."
      maxWidth="md"
    >
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {SUPPORT_TOPICS.map((item) => (
          <Grid item xs={12} sm={4} key={item.title}>
            <Box
              sx={{
                height: '100%',
                p: 2,
                borderRadius: 2,
                bgcolor: brandColors.white,
                border: '1px solid #e2e8f0',
              }}
            >
              <Typography sx={{ fontWeight: 800, color: brandColors.navy, mb: 0.5 }}>
                {item.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                {item.body}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          bgcolor: brandColors.white,
          border: '1px solid #e2e8f0',
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontFamily: '"Sora", Arial, sans-serif',
            color: brandColors.navy,
            mb: 2,
          }}
        >
          Contact us
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {submitted && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSubmitted(false)}>
            Thanks — your support request was submitted. Our team will follow up shortly.
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              SelectProps={{ native: true }}
            >
              {SUPPORT_TOPICS.map((t) => (
                <option key={t.title} value={t.title}>
                  {t.title}
                </option>
              ))}
              <option value="Other">Other</option>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              multiline
              minRows={4}
              label="How can we help?"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </Grid>
        </Grid>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2.5 }} alignItems={{ sm: 'center' }}>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            startIcon={<SendIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: brandColors.navy,
              '&:hover': { bgcolor: '#0a0f28' },
            }}
          >
            {submitting ? 'Sending…' : 'Send message'}
          </Button>
        </Stack>
      </Box>
    </PublicContentPageShell>
  );
};

export default SupportPage;
